import type { TrackingAdapter, TrackingOptions, TrackingUser } from './types'

/**
 * Generic tracker factory.
 *
 * Knows NO concrete SDK (not PostHog, not Screeb, nor any other).
 * Each application injects its matching adapter at initialization time
 * (see libs/tracking/posthog-web for an example).
 *
 * Switching to another provider = create a new adapter that implements
 * `TrackingAdapter`, then change the injection at bootstrap.
 * Zero impact on the apps' business code.
 */
export function createTracker(adapter: TrackingAdapter, enabled = true) {
    return {
    /** À appeler une seule fois au démarrage de l'app. */
        init: (): void => {
            if (enabled) {
                adapter.init()
            }
        },

        /** À appeler après un login réussi. */
        identify: (user: TrackingUser): void => {
            if (enabled) {
                adapter.identify(user)
            }
        },

        /** À appeler au logout. */
        reset: (): void => {
            if (enabled) {
                adapter.reset()
            }
        },

        /**
         * Main function to track feature usage.
         * Convention : always trig an event "feature_used" with the feature name
         * in property, for a coherent "Top features" dashboard regardless of provider choice
         */
        trackFeature: (
            featureName: string,
            metadata: Record<string, unknown> = {},
            options?: TrackingOptions,
        ): void => {
            if (enabled) {
                const properties = { feature: featureName, ...metadata }
                if (options) {
                    adapter.track('feature_used', properties, options)
                } else {
                    adapter.track('feature_used', properties)
                }
            }
        },

        /** For a more generic event, appart from "feature_used". */
        trackEvent: (
            eventName: string,
            properties: Record<string, unknown> = {},
            options?: TrackingOptions,
        ): void => {
            if (enabled) {
                if (options) {
                    adapter.track(eventName, properties, options)
                } else {
                    adapter.track(eventName, properties)
                }
            }
        },

        /** Webview or mobile screen */
        trackPageView: (
            pageName: string,
            properties: Record<string, unknown> = {},
        ): void => {
            if (enabled) {
                adapter.trackPageView(pageName, properties)
            }
        },
    }
}

export type Tracker = ReturnType<typeof createTracker>;
